/**
 * Expenses workspace — ``/stock/expenses`` (§10.1, §10.2).
 *
 * A unified cash-flow timeline. As of the timeline-endpoint rewire
 * (2026-06-04) this page consumes ``GET /api/expenses/timeline`` which
 * merges six event sources (real / drawer expenses, procurement,
 * sales, correction, settlement) into one chronologically-sorted feed.
 *
 * Filter state lives in the URL under the ``e_`` namespace via
 * ``useWorkspaceUrlState({namespace: 'e'})``. Default sort is ``date desc``.
 *
 * Inline edit (Value / Pfand / Category / Comment / Delete) is enabled
 * ONLY on rows with ``source === "real"`` — virtual rows are read-only
 * by definition (procurement/sales/correction/settlement are surfaces
 * onto data owned by other workspaces; drawer rows are bookkeeping
 * artefacts the operator should not edit here).
 *
 * Sign-convention is hidden from the operator. The modal carries a
 * direction radio (Out / In) and the user enters magnitudes; we flip
 * the sign at submit based on the chosen direction. Pfand is an
 * independent cash component (refactor 2026-06-04) — value_cents and
 * pfand_value_cents can each be zero independently of each other but
 * must share a sign when both are non-zero.
 *
 * The table summary footer renders a single dual-Σ row: Value Σ
 * (P&L position, optionally adjusted by four opt-in toggles below the
 * table — open user debts, corporate funding strip, speculative
 * mismatch revenue, speculative revenue on stock) and Pfand Σ (the
 * pfand net position). Sales / procurement-goods / procurement-Pfand
 * rows are no longer footer roll-ups — the virtuals are inline in the
 * table body so the operator sees the per-event detail directly.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { NumInput as InputNumber } from "../../components/NumInput";
import type { ColumnsType } from "antd/es/table";
import dayjs, { type Dayjs } from "dayjs";
import { Link, useSearchParams } from "react-router-dom";

import {
  ApiError,
  type ExpenseCategoryRow,
  type TimelineRow,
  type TimelineSums,
  type VatRateRow,
  api,
} from "../../api/client";
import { useWorkspaceUrlState } from "../../components/workspace";
import { OPERATOR_HIDDEN_FOR_EXPENSES } from "../../utils/expenseCategoryCodes";

const { Title, Text } = Typography;
const { TextArea } = Input;

type Direction = "in" | "out";

interface ExpenseFormValues {
  date: Dayjs;
  category_id: number;
  direction: Direction;
  magnitude_cents: number | null;
  pfand_magnitude_cents: number | null;
  comment?: string | null;
  /** VAT rate id (FK into vat_rate). null = no rate (treated as 0% for net
   * math). Prices stay gross; there's no netto view on this page yet. */
  vat_rate_id?: number | null;
}

function formatSignedCents(c: number | null | undefined): string {
  if (c === null || c === undefined) return "—";
  const euros = c / 100;
  const sign = c > 0 ? "+" : c < 0 ? "−" : "";
  const formatted = new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(Math.abs(euros));
  return `${sign}${formatted}`;
}

/** Unsigned euros formatter for opt-in checkbox labels. The sign /
 * direction is conveyed by the leading ``+`` / ``−`` in the label
 * text — this just renders the magnitude. */
function formatEuros(c: number | null | undefined): string {
  if (c === null || c === undefined) return "€0.00";
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(Math.abs(c) / 100);
}

// Opt-in adjustments below the Expenses table. All four are OFF by
// default — the displayed Value Σ shows realised P&L only unless the
// operator ticks a box. Stored in the URL as ``checked=open_debts,...``
// so a "I always want open debts included" view can be persisted via
// bookmark.
const ALL_CHECKED_KEYS = [
  "open_debts",
  "exclude_corp",
  "mismatch_rev",
  "stock_rev",
] as const;
type CheckedKey = (typeof ALL_CHECKED_KEYS)[number];
const DEFAULT_CHECKED_ROWS: readonly CheckedKey[] = [];

/** Source-kind filter options for the multi-select control. Order
 * mirrors the column-friendliness on the page: "real" expenses first,
 * then the four virtual sources in roughly read-order. Kept in sync
 * with ``TimelineRow.source``. */
const SOURCE_KIND_OPTIONS = [
  { value: "real", label: "Real expense" },
  { value: "drawer", label: "Drawer flow" },
  { value: "procurement", label: "Procurement" },
  { value: "sales", label: "Sales" },
  { value: "correction", label: "Corrections" },
  { value: "settlement", label: "Settlements" },
  { value: "voucher", label: "Vouchers" },
] as const;
type SourceKind = (typeof SOURCE_KIND_OPTIONS)[number]["value"];
const VALID_SOURCE_KINDS = new Set<string>(
  SOURCE_KIND_OPTIONS.map((o) => o.value),
);

type Aggregate = "daily" | "granular";

/** True when the row represents a single, editable real-expense entity.
 *
 * Virtual rows (drawer / procurement / sales / correction / settlement)
 * are owned by other workspaces, AND synthetic aggregate rows (e.g. the
 * per-day stock-loss aggregate in daily mode) are not a single entity
 * either. Both must not show edit / delete affordances on the Expenses
 * table — the discriminator is ``source === "real" && link !== null``.
 * Real expense rows always carry a ``link`` payload from the backend
 * ({type: "expense", id} or {type: "procurement", id}); aggregate rows
 * are emitted with ``link: null``. */
const isEditableExpenseRow = (row: TimelineRow): boolean =>
  row.source === "real" && row.link !== null;

/** Per-source Tag descriptor for the timeline rows. ``real`` returns
 * null so we render no badge — clean default. ``settlement`` is
 * payment-method-aware: we sniff the comment label (the backend emits
 * ``Settlement <receipt> (<method name>)``) and pick a colour + icon
 * accordingly. Anything unknown falls back to a neutral grey. */
function sourceBadge(row: TimelineRow): { color: string; label: string } | null {
  switch (row.source) {
    case "real":
      return null;
    case "drawer":
      return { color: "default", label: "💰 drawer flow" };
    case "procurement":
      return { color: "blue", label: "📦 procurement" };
    case "sales":
      return { color: "green", label: "💳 sales" };
    case "correction":
      return { color: "orange", label: "↺ correction" };
    case "settlement": {
      // Sniff the method label out of the comment: backend emits
      // ``Settlement <receipt> (<payment method name>)``. We only need
      // a coarse colour/icon split — exact method names vary per venue.
      const text = (row.comment ?? "").toLowerCase();
      if (text.includes("cash") || text.includes("bar")) {
        return { color: "gold", label: "💵 Cash" };
      }
      if (text.includes("card") || text.includes("karte")) {
        return { color: "geekblue", label: "💳 Card" };
      }
      if (
        text.includes("bank") ||
        text.includes("transfer") ||
        text.includes("sepa") ||
        text.includes("überweisung")
      ) {
        return { color: "purple", label: "🏦 Bank" };
      }
      if (text.includes("paypal")) {
        return { color: "blue", label: "💳 PayPal" };
      }
      if (text.includes("invoice") || text.includes("rechnung")) {
        return { color: "magenta", label: "🧾 Invoice" };
      }
      return { color: "default", label: "💳 settlement" };
    }
    case "voucher":
      // Display-only reconciliation line (accounting.md §19.2) — never in Σ.
      return { color: "cyan", label: "🎟️ voucher" };
  }
}

export const ExpensesPage = () => {
  const { state, replace } = useWorkspaceUrlState({ namespace: "e" });
  const dateFromFilter = state.filters["date_from"] || "";
  const dateToFilter = state.filters["date_to"] || "";
  const categoryFilter = state.filters["category"] || "";
  const linkedFilter = state.filters["linked"] || "";
  // Source-kind multi-select. Stored as a comma-separated subset of
  // VALID_SOURCE_KINDS; absent / empty = no filter (every source shows).
  const sourceFilterRaw = state.filters["source"] || "";
  const sourceFilter = useMemo<SourceKind[]>(() => {
    if (!sourceFilterRaw) return [];
    return sourceFilterRaw
      .split(",")
      .map((s) => s.trim())
      .filter((s): s is SourceKind => VALID_SOURCE_KINDS.has(s));
  }, [sourceFilterRaw]);
  // Granularity toggle. Default = "daily" (correction/settlement
  // collapsed); "granular" = one row per underlying event.
  const aggregateFilterRaw = state.filters["aggregate"] || "";
  const aggregateFilter: Aggregate =
    aggregateFilterRaw === "granular" ? "granular" : "daily";
  // Parse the `checked` filter (comma-separated row keys) into a Set.
  // Absent → defaults. Present-but-empty → fully unchecked (the
  // operator explicitly turned everything off, valid state).
  const checkedRaw = state.filters["checked"];
  const checkedSet = useMemo<Set<CheckedKey>>(() => {
    if (checkedRaw === undefined) return new Set(DEFAULT_CHECKED_ROWS);
    // ``" "`` (single-space) is the sentinel for "explicitly none" —
    // useWorkspaceUrlState drops empty-string filter values from the
    // URL on serialisation, so we can't store empty as empty.
    if (checkedRaw.trim() === "") return new Set();
    const keys = checkedRaw
      .split(",")
      .map((k) => k.trim())
      .filter((k): k is CheckedKey =>
        (ALL_CHECKED_KEYS as readonly string[]).includes(k),
      );
    return new Set(keys);
  }, [checkedRaw]);
  const enabled = useCallback(
    (key: CheckedKey) => checkedSet.has(key),
    [checkedSet],
  );
  const toggleChecked = useCallback(
    (key: CheckedKey, on: boolean) => {
      const next = new Set(checkedSet);
      if (on) next.add(key);
      else next.delete(key);
      // Serialise in the canonical ALL_CHECKED_KEYS order so the URL
      // is stable regardless of click order. Empty set is stored as
      // an empty string (sentinel) so we can distinguish "explicitly
      // none" from "absent, use defaults" on re-parse.
      const serialised = ALL_CHECKED_KEYS.filter((k) => next.has(k)).join(",");
      const nextFilters = { ...state.filters };
      if (next.size === 0) {
        // Empty-but-present: store an empty-string-equivalent sentinel.
        // useWorkspaceUrlState drops empty-string filter values from
        // the URL, which would re-trigger the defaults. Use a single
        // space (URL-encoded as "+") as the sentinel; checkedRaw
        // === " " parses to an empty set above.
        nextFilters["checked"] = " ";
      } else {
        nextFilters["checked"] = serialised;
      }
      replace({ filters: nextFilters });
    },
    [checkedSet, replace, state.filters],
  );

  const [rows, setRows] = useState<TimelineRow[]>([]);
  const [sums, setSums] = useState<TimelineSums | null>(null);
  const [categories, setCategories] = useState<ExpenseCategoryRow[]>([]);
  // VAT rates (incl. archived so a row pointing at a retired rate still
  // resolves in the edit selector). Powers the edit-modal VAT dropdown.
  const [vatRates, setVatRates] = useState<VatRateRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ?focus=<id> URL param — links from other workspaces (e.g. the cash
  // drawer activity feed → /stock/expenses?focus=42) deep-link to a
  // specific row. After the table loads, we scroll the row into view
  // and apply a highlight style via onRow. Only ``source === "real"``
  // rows are valid focus targets — the cash drawer activity feed links
  // to expense IDs, which are real rows. For non-real rows that ?focus=
  // might point at (e.g. a stale link after a refactor), the lookup is
  // a no-op.
  const [searchParams] = useSearchParams();
  const focusId = useMemo(() => {
    const raw = searchParams.get("focus");
    const n = raw !== null ? Number(raw) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [searchParams]);

  // Create / edit modal state.
  const [modalOpen, setModalOpen] = useState(false);
  // Editing always tracks a "real" timeline row (virtual rows have no
  // edit UI). We store the full row so the modal opener can read the
  // operator-relevant fields without a follow-up fetch.
  const [editing, setEditing] = useState<TimelineRow | null>(null);
  const [form] = Form.useForm<ExpenseFormValues>();
  // Watched fields so we can hide the Value input when the selected
  // category is "Pfand" and so the direction radio drives the inline
  // styling.
  const watchedCategoryId = Form.useWatch("category_id", form);
  const watchedDirection = Form.useWatch("direction", form);

  // Default sort: date desc. We keep the URL sort field optional, falling
  // back to date when nothing's set.
  const sortField = state.sort ?? "date";
  const sortDir = state.sortDir;

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: {
        from?: string;
        to?: string;
        category?: number;
        linked?: string;
        source?: string[];
        aggregate?: Aggregate;
      } = {};
      if (dateFromFilter) params.from = dateFromFilter;
      if (dateToFilter) params.to = dateToFilter;
      if (categoryFilter) {
        const n = Number(categoryFilter);
        if (!Number.isNaN(n)) params.category = n;
      }
      // Only the "procurement" linked filter is meaningful server-side;
      // the rest are client-side derivations (see the Linked column).
      if (linkedFilter === "procurement") params.linked = "procurement";
      // Empty list → omit so the backend sees "no filter" and returns
      // every source. Same for aggregate=daily (the server default).
      if (sourceFilter.length > 0) params.source = [...sourceFilter];
      if (aggregateFilter === "granular") params.aggregate = "granular";
      const [timeline, cats] = await Promise.all([
        api.getExpensesTimeline(params),
        api.listExpenseCategories({ include_archived: true }),
      ]);
      setRows(timeline.rows);
      setSums(timeline.sums);
      setCategories(cats);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [
    dateFromFilter,
    dateToFilter,
    categoryFilter,
    linkedFilter,
    sourceFilter,
    aggregateFilter,
  ]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // VAT rates are filter-independent — load once on mount. A hiccup just
  // leaves the selector empty (the edit modal still works without a rate).
  useEffect(() => {
    let cancelled = false;
    void api
      .listVatRates(true)
      .then((rs) => {
        if (!cancelled) setVatRates(rs);
      })
      .catch(() => {
        if (!cancelled) setVatRates([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Scroll the ?focus=<id> row into view once the table data is rendered.
  // Uses AntD's auto-applied data-row-key attribute on <tr>. The
  // requestAnimationFrame defers the lookup by one paint so the DOM has
  // settled after rows[] state propagates through Table's render. If
  // the focused row isn't currently rendered (e.g. on a different
  // pagination page or filtered out, or it's a virtual row that ?focus
  // can't actually match), the lookup is a no-op — the operator can
  // then page/filter manually to surface it.
  useEffect(() => {
    if (focusId === null || rows.length === 0) return;
    // ?focus= MUST resolve to a "real" timeline row — virtual rows
    // share the id-space with other tables and a numeric collision
    // would scroll the wrong row into view.
    const target = rows.find((r) => r.id === focusId && r.source === "real");
    if (!target) return;
    const handle = window.requestAnimationFrame(() => {
      const el = document.querySelector(
        `tr[data-row-key="${rowKey(target)}"]`,
      );
      if (el instanceof HTMLElement) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
    return () => window.cancelAnimationFrame(handle);
  }, [focusId, rows]);

  // Pfand category id — used to hide the Value input when "Pfand" is
  // the chosen category (Pfand rows are by definition value=0, the
  // amount lives in the Pfand input). Looked up by immutable ``code``
  // (post-migration-0024) rather than display name — operators may
  // rename "Pfand" freely; the code stays stable.
  const pfandCategoryId = useMemo(() => {
    const m = categories.find((c) => c.code === "pfand");
    return m?.id ?? null;
  }, [categories]);

  const hideValueInput =
    pfandCategoryId !== null && watchedCategoryId === pfandCategoryId;

  const catLookup = useMemo(() => {
    const m = new Map<number, ExpenseCategoryRow>();
    for (const c of categories) m.set(c.id, c);
    return m;
  }, [categories]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      let v: number = 0;
      switch (sortField) {
        case "date":
          // sort_key carries the ISO datetime — use it directly so
          // mixed virtual / real rows on the same calendar day stay
          // in the same chronological order the backend produced.
          v = (a.sort_key ?? "").localeCompare(b.sort_key ?? "");
          break;
        case "comment":
          v = (a.comment ?? "").localeCompare(b.comment ?? "");
          break;
        case "category":
          // Virtual rows have no category — they sort to the start
          // (empty-string lookup), which is fine: stable bucketing.
          v = (
            (a.category_id !== null
              ? catLookup.get(a.category_id)?.name
              : null) ?? ""
          ).localeCompare(
            (b.category_id !== null
              ? catLookup.get(b.category_id)?.name
              : null) ?? "",
          );
          break;
        case "value":
          v = a.value_cents - b.value_cents;
          break;
        case "pfand_value":
          v = (a.pfand_value_cents ?? 0) - (b.pfand_value_cents ?? 0);
          break;
        case "source":
          v = a.source.localeCompare(b.source);
          break;
        default:
          v = (a.sort_key ?? "").localeCompare(b.sort_key ?? "");
      }
      return sortDir === "asc" ? v : -v;
    });
    return copy;
  }, [rows, sortField, sortDir, catLookup]);

  // Client-side recompute of Σ value + Σ Pfand over the currently visible
  // row set so the bottom-row totals always reflect what the operator
  // sees on screen — across all pages of the current filter, not just
  // the current pagination slice. Mirrors the server-side exclusion
  // rules in routes/expenses.py:1132-1173 (drawer rows + Pfand-category
  // real rows excluded from the value Σ; settlement rows carry no Pfand
  // so excluded from the Pfand Σ). Result is income-positive
  // (= positive means profit) to match server-supplied scalars.
  //
  // Why client-side: with passive filters this matches the server's
  // sums.value_p_and_l_cents exactly; with any narrowing filter active
  // it stays in sync with whatever the table shows, removing the
  // possibility of bottom-Σ drift when filters interact in subtle ways.
  const _DRAWER_CATEGORY_CODES = useMemo(
    () => new Set(["cash_drawer", "cash_drawer_opening", "cash_variance"]),
    [],
  );
  // Single source of truth for "does this row's VALUE count toward the Σ".
  // Used BOTH by the Σ computation and by the table (rows whose value is
  // excluded render their Value in brackets) so the two can never drift.
  // Excluded: drawer rows (cash-drawer bookkeeping), settlement rows (a
  // guest paying their tab is a cash movement, not P&L) and Pfand-category
  // real rows (their value is 0 — the amount lives in the Pfand column).
  const valueInSum = useCallback(
    (r: TimelineRow): boolean => {
      if (r.source === "real") {
        const code =
          r.category_id !== null
            ? (catLookup.get(r.category_id)?.code ?? null)
            : null;
        if (code !== null && _DRAWER_CATEGORY_CODES.has(code)) return false;
        if (code === "pfand") return false;
        return true;
      }
      return (
        r.source === "procurement" ||
        r.source === "sales" ||
        r.source === "correction"
      );
    },
    [catLookup, _DRAWER_CATEGORY_CODES],
  );
  const { visibleValuePnL, visiblePfandPosition, visibleSettlementsTotal } =
    useMemo(() => {
      let costPositiveValue = 0;
      let costPositivePfand = 0;
      let settlements = 0;
      for (const r of sortedRows) {
        if (valueInSum(r)) {
          // Corrections already arrive cost-positive; everything else is
          // bank-statement-signed, so flip to cost-positive here.
          costPositiveValue +=
            r.source === "correction" ? r.value_cents : -r.value_cents;
        }
        if (
          r.pfand_value_cents !== null &&
          r.pfand_value_cents !== 0 &&
          r.source !== "settlement"
        ) {
          costPositivePfand += -r.pfand_value_cents;
        }
        // Informational only (NOT in the Σ): total guests have already
        // settled (paid their tab) in the visible window — the paid
        // counterpart to "open user debts" (the unpaid part).
        if (r.source === "settlement") {
          settlements += r.value_cents;
        }
      }
      return {
        visibleValuePnL: -costPositiveValue,
        visiblePfandPosition: -costPositivePfand,
        visibleSettlementsTotal: settlements,
      };
    }, [sortedRows, valueInSum]);

  const openCreateModal = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      date: dayjs(),
      direction: "out",
      magnitude_cents: null,
      pfand_magnitude_cents: null,
      comment: null,
      // 0 = the "No VAT" option (stored as null on submit).
      vat_rate_id: 0,
    });
    setModalOpen(true);
  };

  const openEditValueModal = (row: TimelineRow) => {
    // Only real rows are editable — virtual rows are owned by other
    // workspaces and have no PATCH /expenses surface.
    if (!isEditableExpenseRow(row) || row.category_id === null) return;
    setEditing(row);
    // Pick the direction from whichever cash component is non-zero.
    // A Pfand-only row (value=0) reads its sign from pfand; a
    // value-only row reads from value. Both being zero is impossible
    // by route-layer validation.
    const referenceCents =
      row.value_cents !== 0 ? row.value_cents : (row.pfand_value_cents ?? 0);
    const direction: Direction = referenceCents >= 0 ? "in" : "out";
    form.resetFields();
    form.setFieldsValue({
      date: dayjs(row.date),
      category_id: row.category_id,
      direction,
      magnitude_cents:
        row.value_cents === 0 ? null : Math.abs(row.value_cents),
      pfand_magnitude_cents:
        row.pfand_value_cents === null || row.pfand_value_cents === 0
          ? null
          : Math.abs(row.pfand_value_cents),
      comment: row.comment ?? null,
      // null → 0 so the explicit "No VAT" option shows as selected.
      vat_rate_id: row.vat_rate_id ?? 0,
    });
    setModalOpen(true);
  };

  const onSubmitModal = async () => {
    let values: ExpenseFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const sign = values.direction === "in" ? 1 : -1;
    // Pfand category hides the value input — treat the magnitude as 0
    // in that branch so a Pfand-only row goes through with value=0.
    const effectiveValueMagnitude =
      pfandCategoryId !== null && values.category_id === pfandCategoryId
        ? 0
        : values.magnitude_cents ?? 0;
    const value_cents = sign * Math.round(effectiveValueMagnitude);
    const pfand_value_cents =
      values.pfand_magnitude_cents === null ||
      values.pfand_magnitude_cents === undefined ||
      values.pfand_magnitude_cents === 0
        ? null
        : sign * Math.round(values.pfand_magnitude_cents);
    // Require at least one of value / pfand to carry a magnitude — the
    // schema CHECK rejects an all-zero row at insert time, but a clean
    // 400 from the route layer is friendlier.
    if (
      (value_cents === 0 || Number.isNaN(value_cents)) &&
      (pfand_value_cents === null || pfand_value_cents === 0)
    ) {
      setError("Enter a value or a Pfand amount.");
      return;
    }
    const payload = {
      date: values.date.format("YYYY-MM-DD"),
      category_id: values.category_id,
      value_cents,
      pfand_value_cents,
      comment: values.comment ?? null,
      // 0 = "No VAT" sentinel → store null (no rate).
      vat_rate_id: values.vat_rate_id ? values.vat_rate_id : null,
    };
    try {
      if (editing) {
        // The timeline endpoint doesn't return updated_at; for the
        // edit-in-place flow we skip the If-Match guard. A future
        // hardening pass can extend TimelineRow with the timestamp
        // and route through patchExpense with the precondition.
        await api.patchExpense(editing.id, payload);
      } else {
        await api.createExpense(payload);
      }
      setModalOpen(false);
      setEditing(null);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const onDelete = async (row: TimelineRow) => {
    if (!isEditableExpenseRow(row)) return;
    try {
      await api.deleteExpense(row.id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  // Inline category change — real rows only. Without an updated_at
  // from the timeline endpoint we omit If-Match (last-write-wins on a
  // single-field PATCH is acceptable on the operator-controlled
  // expenses workspace).
  const onChangeCategory = async (row: TimelineRow, nextId: number) => {
    if (!isEditableExpenseRow(row)) return;
    if (nextId === row.category_id) return;
    try {
      await api.patchExpense(row.id, { category_id: nextId });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  // Inline comment commit on blur. Skip if unchanged. Real rows only.
  const onCommitComment = async (row: TimelineRow, next: string) => {
    if (!isEditableExpenseRow(row)) return;
    const cleaned = next.trim() ? next : null;
    if (cleaned === row.comment) return;
    try {
      await api.patchExpense(row.id, { comment: cleaned });
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const setFilter = (key: string, value: string) => {
    const next = { ...state.filters };
    if (value) next[key] = value;
    else delete next[key];
    replace({ filters: next });
  };

  const setSort = (col: string) => {
    if (state.sort === col) {
      replace({ sortDir: sortDir === "asc" ? "desc" : "asc" });
    } else {
      replace({ sort: col, sortDir: "desc" });
    }
  };

  const sortOrderFor = (col: string): "ascend" | "descend" | undefined => {
    if (sortField !== col) return undefined;
    return sortDir === "asc" ? "ascend" : "descend";
  };

  const columns: ColumnsType<TimelineRow> = useMemo(
    () => [
      {
        title: "#",
        dataIndex: "id",
        key: "id",
        width: 80,
        align: "right",
        sorter: true,
        sortOrder: sortOrderFor("id"),
        onHeaderCell: () => ({ onClick: () => setSort("id") }),
        render: (id: number, row) => (
          <Text type="secondary" style={{ fontVariantNumeric: "tabular-nums" }}>
            {isEditableExpenseRow(row) ? `#${id}` : "—"}
          </Text>
        ),
      },
      {
        title: "Source",
        key: "source",
        width: 150,
        sorter: true,
        sortOrder: sortOrderFor("source"),
        onHeaderCell: () => ({ onClick: () => setSort("source") }),
        render: (_: unknown, row) => {
          const b = sourceBadge(row);
          if (!b) return null;
          return <Tag color={b.color}>{b.label}</Tag>;
        },
      },
      {
        title: "Date",
        dataIndex: "date",
        key: "date",
        width: 100,
        sorter: true,
        sortOrder: sortOrderFor("date"),
        onHeaderCell: () => ({ onClick: () => setSort("date") }),
        render: (v: string) => dayjs(v).format("DD.MM.YYYY"),
      },
      {
        title: "Comment",
        dataIndex: "comment",
        key: "comment",
        sorter: true,
        sortOrder: sortOrderFor("comment"),
        onHeaderCell: () => ({ onClick: () => setSort("comment") }),
        render: (_: string | null, row) => {
          // Virtual rows + synthetic aggregates (e.g. per-day stock
          // loss) are read-only; their Source-column tag is the
          // canonical indicator, and there's no single underlying
          // entity to PATCH the comment back to. Real expense rows
          // (link !== null) get the inline TextArea editor.
          if (!isEditableExpenseRow(row)) {
            return <Text type="secondary">{row.comment ?? "—"}</Text>;
          }
          return (
            <Tooltip title={row.comment ?? ""}>
              <TextArea
                size="small"
                defaultValue={row.comment ?? ""}
                autoSize={{ minRows: 1, maxRows: 3 }}
                onBlur={(e) => void onCommitComment(row, e.target.value)}
                placeholder="—"
              />
            </Tooltip>
          );
        },
      },
      {
        title: "Category",
        dataIndex: "category_id",
        key: "category",
        width: 200,
        sorter: true,
        sortOrder: sortOrderFor("category"),
        onHeaderCell: () => ({ onClick: () => setSort("category") }),
        filteredValue: categoryFilter ? [Number(categoryFilter)] : null,
        filters: categories
          .filter((c) => c.active || c.id === Number(categoryFilter))
          .map((c) => ({
            text: c.name + (c.system_seeded ? " (system)" : ""),
            value: c.id,
          })),
        onFilter: (value, record) => record.category_id === value,
        render: (catId: number | null, row) => {
          // Virtual rows + synthetic aggregates have no editable
          // category — show the static category name (preserved on
          // the aggregate row so the operator still sees "Stock loss"
          // etc.) with the sys badge inline, or "—" when missing.
          if (!isEditableExpenseRow(row)) {
            if (catId === null) {
              return <Text type="secondary">—</Text>;
            }
            const cat = catLookup.get(catId);
            return (
              <Space size={4}>
                <Text>{cat?.name ?? row.category_name ?? "—"}</Text>
                {cat?.system_seeded && <Tag color="blue">sys</Tag>}
              </Space>
            );
          }
          if (catId === null) {
            return <Text type="secondary">—</Text>;
          }
          const cat = catLookup.get(catId);
          return (
            <Select
              size="small"
              style={{ width: "100%" }}
              value={catId}
              onChange={(v) => void onChangeCategory(row, v)}
              options={categories
                .filter((c) => c.active || c.id === catId)
                .map((c) => ({
                  value: c.id,
                  label: c.name,
                }))}
              dropdownMatchSelectWidth={false}
              suffixIcon={
                cat?.system_seeded ? <Tag color="blue">sys</Tag> : undefined
              }
            />
          );
        },
      },
      {
        // Pfand BEFORE Value — matches the procurement extras table
        // (ProcurementDetailPage) where the same swap was applied so
        // the two tables read consistently.
        title: "Pfand",
        dataIndex: "pfand_value_cents",
        key: "pfand_value",
        width: 110,
        align: "right",
        sorter: true,
        sortOrder: sortOrderFor("pfand_value"),
        onHeaderCell: () => ({ onClick: () => setSort("pfand_value") }),
        render: (v: number | null) =>
          // null OR 0 → dash (no "0,00 €" noise; the row carries no Pfand).
          v === null || v === 0 ? (
            <Text type="secondary">—</Text>
          ) : (
            <Text
              style={{ color: v > 0 ? "#52c41a" : v < 0 ? "#cf1322" : undefined }}
            >
              {formatSignedCents(v)}
            </Text>
          ),
      },
      {
        title: "Value",
        dataIndex: "value_cents",
        key: "value",
        width: 130,
        align: "right",
        sorter: true,
        sortOrder: sortOrderFor("value"),
        onHeaderCell: () => ({ onClick: () => setSort("value") }),
        render: (v: number, row) => {
          // Pfand-category real expenses with value=0 are pure Pfand
          // flows (no cash crossed the till) — render a "—" so the
          // Value cell doesn't pretend to carry information it doesn't.
          if (
            row.source === "real" &&
            v === 0 &&
            row.category_id !== null &&
            catLookup.get(row.category_id)?.code === "pfand"
          ) {
            return <Text type="secondary">—</Text>;
          }
          const text = formatSignedCents(v);
          // Values NOT counted in the Σ (drawer bookkeeping + settlement
          // cash movements) render in (brackets) + muted so it's clear at a
          // glance they're informational. Drawer keeps its italic tell.
          if (!valueInSum(row)) {
            return (
              <Text
                type="secondary"
                italic={row.source === "drawer"}
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                ({text})
              </Text>
            );
          }
          return (
            <Text
              style={{
                color: v > 0 ? "#52c41a" : v < 0 ? "#cf1322" : undefined,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {text}
            </Text>
          );
        },
      },
      {
        title: "Action",
        key: "actions",
        width: 140,
        render: (_: unknown, row) => {
          // Actions are real-only. Virtual rows expose a deep-link to
          // the originating workspace via the row's ``link`` payload
          // from the backend. Synthetic aggregates (source="real",
          // link=null) have no Edit / Delete and no link target —
          // they render nothing.
          if (!isEditableExpenseRow(row)) {
            if (row.link) {
              const { type, id } = row.link;
              const target =
                type === "procurement"
                  ? `/stock/procurement/${id}`
                  : type === "correction"
                    ? `/corrections`
                    : type === "settlement"
                      ? `/settlements`
                      : null;
              if (target) {
                return (
                  <Link to={target}>
                    <Button size="small" type="link">
                      Open →
                    </Button>
                  </Link>
                );
              }
            }
            return null;
          }
          return (
            <Space size={4}>
              <Button size="small" onClick={() => openEditValueModal(row)}>
                Edit
              </Button>
              <Popconfirm
                title="Delete this expense?"
                description="Soft-delete — the row is hidden but kept for audit."
                onConfirm={() => void onDelete(row)}
              >
                <Button danger size="small">
                  Delete
                </Button>
              </Popconfirm>
            </Space>
          );
        },
      },
    ],
    // ``setSort``, ``onCommitComment``, etc. are closures over current state
    // and recreated on every render; intentional, the alternative would be
    // to memoise them all with useCallback and add them as deps. Re-rendering
    // the header on filter change is cheap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categories, catLookup, sortField, sortDir, categoryFilter, linkedFilter],
  );

  return (
    <Card>
      <Space
        style={{
          width: "100%",
          justifyContent: "space-between",
          marginBottom: 12,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Expenses
        </Title>
        <Space wrap>
          <Button type="primary" onClick={() => openCreateModal()}>
            + Add expense
          </Button>
        </Space>
      </Space>

      <Space
        wrap
        style={{
          marginBottom: 12,
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <Space wrap>
          <Space>
            <Text type="secondary">From</Text>
            <DatePicker
              value={dateFromFilter ? dayjs(dateFromFilter) : null}
              onChange={(d) =>
                setFilter("date_from", d ? d.format("YYYY-MM-DD") : "")
              }
              allowClear
              format="DD.MM.YYYY"
            />
          </Space>
          <Space>
            <Text type="secondary">To</Text>
            <DatePicker
              value={dateToFilter ? dayjs(dateToFilter) : null}
              onChange={(d) =>
                setFilter("date_to", d ? d.format("YYYY-MM-DD") : "")
              }
              allowClear
              format="DD.MM.YYYY"
            />
          </Space>
          {/* Source-kind multi-select. Empty = no filter (every source
              shows); ticking a subset fires the api with ?source=... per
              entry. Stored under the ``source`` filter key, comma-joined
              for URL stability. */}
          <Space>
            <Text type="secondary">Sources</Text>
            <Select<SourceKind[]>
              mode="multiple"
              allowClear
              placeholder="All sources"
              value={sourceFilter}
              onChange={(values) => {
                // Serialise in canonical option order so URL stays stable
                // regardless of click order.
                const ordered = SOURCE_KIND_OPTIONS.map((o) => o.value).filter(
                  (v) => values.includes(v),
                );
                setFilter("source", ordered.join(","));
              }}
              options={SOURCE_KIND_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
              style={{ minWidth: 240 }}
              maxTagCount="responsive"
            />
          </Space>
          {/* Reset everything (date range + category + linked-source +
              source-kind + aggregate + venue-summary checkbox state +
              sort) to defaults. Visible only when at least one filter
              or non-default sort is set, so a fresh page load doesn't
              carry a no-op button. */}
          {(dateFromFilter ||
            dateToFilter ||
            categoryFilter ||
            linkedFilter ||
            sourceFilterRaw ||
            aggregateFilterRaw ||
            checkedRaw !== undefined ||
            state.sort !== undefined ||
            state.sortDir !== undefined) && (
            <Button
              onClick={() =>
                replace({
                  filters: {},
                  sort: undefined,
                  sortDir: undefined,
                })
              }
            >
              Reset filters
            </Button>
          )}
        </Space>
        {/* Granularity toggle — top-right of the filter row, paired with
            the table card below. Default = "Daily aggregate"
            (correction/settlement rows collapsed per-day);
            "Granular" = one row per underlying event. URL key:
            ``aggregate`` ("granular" present | absent). */}
        <Segmented<Aggregate>
          value={aggregateFilter}
          onChange={(value) =>
            setFilter("aggregate", value === "granular" ? "granular" : "")
          }
          options={[
            { value: "daily", label: "Daily aggregate" },
            { value: "granular", label: "Granular" },
          ]}
        />
      </Space>

      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table<TimelineRow>
        columns={columns}
        dataSource={sortedRows}
        // Virtual rows share the numeric id-space with each other and
        // with real rows — disambiguate by namespacing the key with
        // ``source``. React keys must be globally unique across the
        // dataSource, otherwise re-renders smear state between rows.
        rowKey={(r) => rowKey(r)}
        loading={loading}
        pagination={{ pageSize: 50, showSizeChanger: false }}
        size="middle"
        // Highlight a ?focus=<id> row so deep-links from the cash drawer
        // activity feed / other workspaces visually land on the target.
        // Only real rows are valid focus targets (see useEffect above).
        onRow={(row) => ({
          style:
            focusId !== null && row.id === focusId && row.source === "real"
              ? {
                  background: "var(--ant-color-warning-bg, #fff7e6)",
                  outline: "2px solid var(--ant-color-warning-border, #ffd591)",
                }
              : undefined,
        })}
        onChange={(_pagination, filters) => {
          const nextCat = (filters.category as (number | string)[] | null)?.[0];
          setFilter("category", nextCat != null ? String(nextCat) : "");
        }}
        summary={() => {
          // Single Σ row. Base values come from the CLIENT-SIDE recompute
          // over the visible row set (visibleValuePnL / visiblePfandPosition)
          // so the totals always match what's on screen across all pages
          // of the current filter. The four opt-in adjustments are
          // lifetime venue scalars from the server and add on top.
          const valuePnL = visibleValuePnL;
          const pfandPosition = visiblePfandPosition;
          const displayedValueSum =
            valuePnL -
            // SUBTRACT, not add: the base Σ already counts every drink as
            // income (sales rows) whether paid or not, so open debts are
            // the unpaid slice of that — subtracting yields the cash-realized
            // view. Adding would double-count against sales.
            (enabled("open_debts") ? sums?.open_user_debts_cents ?? 0 : 0) -
            (enabled("exclude_corp")
              ? sums?.corporate_funding_cents ?? 0
              : 0) +
            (enabled("mismatch_rev")
              ? sums?.mismatch_revenue_cents ?? 0
              : 0) +
            (enabled("stock_rev") ? sums?.revenue_on_stock_cents ?? 0 : 0);
          // Column layout (8 columns) after the Pfand←→Value swap:
          // 0 # | 1 Source | 2 Date | 3 Comment | 4 Category
          //   | 5 Pfand | 6 Value | 7 Action
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={5}>
                <Text strong>Σ</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={5} align="right">
                <Text
                  strong
                  style={{
                    color:
                      pfandPosition > 0
                        ? "#52c41a"
                        : pfandPosition < 0
                          ? "#cf1322"
                          : undefined,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatSignedCents(pfandPosition)}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">
                <Text
                  strong
                  style={{
                    color:
                      displayedValueSum > 0
                        ? "#52c41a"
                        : displayedValueSum < 0
                          ? "#cf1322"
                          : undefined,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatSignedCents(displayedValueSum)}
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} />
            </Table.Summary.Row>
          );
        }}
      />

      {/* Opt-in adjustments to the Value Σ above. All four default OFF;
          ticking a box recomputes the displayed Σ. State lives in the
          URL ``checked=`` filter so a bookmark can persist a preferred
          view (e.g. "always include open user debts"). */}
      {sums && (
        <Space wrap size={[16, 8]} style={{ marginTop: 12 }}>
          <Checkbox
            checked={enabled("open_debts")}
            onChange={(e) => toggleChecked("open_debts", e.target.checked)}
          >
            Subtract open user debts{" "}
            <Text type="secondary">
              (−{formatEuros(sums.open_user_debts_cents)} — drinks consumed
              but not yet paid)
            </Text>
          </Checkbox>
          <Checkbox
            checked={enabled("exclude_corp")}
            onChange={(e) => toggleChecked("exclude_corp", e.target.checked)}
          >
            Exclude corporate funding{" "}
            <Text type="secondary">
              (−{formatEuros(sums.corporate_funding_cents)} — strip
              corporate support)
            </Text>
          </Checkbox>
          <Checkbox
            checked={enabled("mismatch_rev")}
            onChange={(e) => toggleChecked("mismatch_rev", e.target.checked)}
          >
            Mismatch revenue{" "}
            <Text type="secondary">
              (+{formatEuros(sums.mismatch_revenue_cents)} — speculative)
            </Text>
          </Checkbox>
          <Checkbox
            checked={enabled("stock_rev")}
            onChange={(e) => toggleChecked("stock_rev", e.target.checked)}
          >
            Revenue on stock{" "}
            <Text type="secondary">
              (+{formatEuros(sums.revenue_on_stock_cents)} — speculative)
            </Text>
          </Checkbox>
        </Space>
      )}

      {/* Informational only — the paid counterpart to "open user debts":
          how much guests have already settled in the visible window. Shown
          in brackets because it's NOT part of the Σ (settlements are cash
          movements, not P&L). */}
      {visibleSettlementsTotal !== 0 && (
        <div style={{ marginTop: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Already settled by guests (paid — not in Σ):{" "}
            <Text strong>({formatSignedCents(visibleSettlementsTotal)})</Text>
          </Text>
        </div>
      )}

      <Modal
        open={modalOpen}
        title={editing ? "Edit expense" : "Add expense"}
        okText={editing ? "Save" : "Add"}
        onOk={() => void onSubmitModal()}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        destroyOnHidden
      >
        <Form<ExpenseFormValues>
          form={form}
          layout="vertical"
          initialValues={{
            date: dayjs(),
            direction: "out",
            magnitude_cents: null,
            pfand_magnitude_cents: null,
            comment: null,
          }}
        >
          <Form.Item
            label="Date"
            name="date"
            rules={[{ required: true, message: "Date is required" }]}
          >
            <DatePicker format="DD.MM.YYYY" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Category"
            name="category_id"
            rules={[{ required: true, message: "Category is required" }]}
          >
            <Select
              placeholder="Select category"
              options={categories
                .filter(
                  (c) =>
                    c.active &&
                    !(OPERATOR_HIDDEN_FOR_EXPENSES as readonly string[]).includes(
                      c.code,
                    ),
                )
                .map((c) => ({
                  value: c.id,
                  label: c.name + (c.system_seeded ? " (system)" : ""),
                }))}
            />
          </Form.Item>
          <Form.Item
            label="Direction"
            name="direction"
            rules={[{ required: true, message: "Direction is required" }]}
          >
            <Radio.Group buttonStyle="solid">
              <Radio.Button
                value="in"
                style={{
                  // Inline coloring per the 2026-06-04 redesign: the
                  // selected button picks up the green/red tint so
                  // the cash-flow direction is unambiguous at a glance.
                  ...(watchedDirection === "in"
                    ? {
                        borderColor: "#52c41a",
                        color: "#52c41a",
                      }
                    : {}),
                }}
              >
                ↑ In (refund / Pfand return)
              </Radio.Button>
              <Radio.Button
                value="out"
                style={{
                  ...(watchedDirection === "out"
                    ? {
                        borderColor: "#cf1322",
                        color: "#cf1322",
                      }
                    : {}),
                }}
              >
                ↓ Out (spent)
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
          {!hideValueInput && (
            <Form.Item
              label="Value"
              name="magnitude_cents"
              tooltip="Non-Pfand cash portion in cents. Sign comes from the direction above."
            >
              <InputNumber
                min={0}
                step={1}
                addonAfter="ct"
                style={{ width: "100%" }}
              />
            </Form.Item>
          )}
          <Form.Item
            label="Pfand"
            name="pfand_magnitude_cents"
            tooltip="Independent of the value above (2026-06-04 refactor), in cents. Leave empty for no Pfand component."
          >
            <InputNumber
              min={0}
              step={1}
              addonAfter="ct"
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            label="VAT"
            name="vat_rate_id"
            tooltip="VAT classification for accounting. The price stays gross; pick 'No VAT' for no rate (e.g. no receipt)."
          >
            <Select
              placeholder="No VAT rate"
              // value 0 = explicit "No VAT" (→ stored as null); vat_rate ids
              // start at 1, so 0 is a safe sentinel.
              options={[
                { value: 0, label: "No VAT" },
                ...vatRates
                  .filter(
                    (v) =>
                      v.active || v.id === form.getFieldValue("vat_rate_id"),
                  )
                  .map((v) => ({
                    value: v.id,
                    label: `${Number(v.percent)}% ${v.name}`,
                  })),
              ]}
            />
          </Form.Item>
          <Form.Item label="Comment" name="comment">
            <TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

/** Globally-unique row key for a TimelineRow. Virtual sources share
 * the numeric id-space (e.g. a procurement id can collide with an
 * expense id), so we namespace by source. */
function rowKey(r: TimelineRow): string {
  return `${r.source}-${r.id}`;
}

export default ExpensesPage;
